/**
 * Document resources as editor commands: notes, artboards, export assets,
 * slices, alpha channels, perspective planes, layer comps, global swatches,
 * recorded actions and tool presets, plus the layer operations their panels
 * run (image trace, group disclosure, colour reduction, perspective mapping).
 *
 * Every command is ID-addressed and declarative. Each compiles to the native
 * command the panel or dialog executed before, built from the document as it
 * is when the transaction runs, so the UI keeps byte-identical results and
 * history labels. New entities get their IDs from the same constructors as
 * before (`createId`); agents learn them from the journal, which reports
 * `extensions.<key>[<id>]` entries for keyed resource lists.
 *
 * Footprints name the resource key (DOCUMENT_RESOURCES) plus the layers an
 * operation reads or writes. Operations that write layers (applying a comp or
 * a swatch, recolouring linked paint, mapping pixels) refuse locked layers
 * for agents; the user keeps native semantics (ActorPolicy).
 */
import type { Ref } from '@maipo/agent-protocol';
import { contentLabel } from '@maipo/agent-protocol';
import { DeleteActionCommand, documentActions, RenameActionCommand } from '../../core/actions';
import { alphaChannels, SetAlphaChannelsCommand } from '../../core/channels';
import { AddLayerCommand, SetGroupExpandedCommand } from '../../core/commands';
import {
  artboards,
  createArtboard,
  createExportAsset,
  createSlice,
  exportAssets,
  SetArtboardsCommand,
  SetExportAssetsCommand,
  SetSlicesCommand,
  slices,
  slicesFromGuides,
  type Artboard,
  type ExportAsset,
  type Slice,
} from '../../core/export-resources';
import { invertMatrix, transformPoint, type Point } from '../../core/geometry';
import { ApplyGlobalSwatchCommand, createGlobalSwatch, globalSwatches, linkedPaints, SetGlobalSwatchesCommand, UpdateGlobalSwatchCommand } from '../../core/global-swatches';
import { ApplyLayerCompCommand, captureLayerComp, layerComps, SetLayerCompsCommand, type LayerComp } from '../../core/layer-comps';
import { findLayer, findLayerParent, layerDocumentTransform, walkLayers, type EditorDocument, type Layer } from '../../core/model';
import { createDocumentNote, documentNotes, SetDocumentNotesCommand, type DocumentNote } from '../../core/notes';
import {
  activePerspectivePlane,
  createPerspectivePlane,
  perspectivePlanes,
  SetPerspectivePlanesCommand,
  validPerspectiveQuad,
  warpRasterToPerspectivePlane,
  type PerspectiveGridMode,
  type PerspectivePlane,
  type Quad,
} from '../../core/perspective';
import { ReplaceRasterCommand } from '../../core/template';
import { SetToolPresetsCommand, toolPresets } from '../../core/tool-presets';
import { layerColourStack, ReduceColoursCommand } from '../../vector/colour-stack';
import { traceRasterLayer } from '../../vector/image-trace';
import { IntentRejection, type Diagnostic, type Footprint, type ValidateContext } from '../registry';
import { checkContainerUnlocked, checkUnlocked, error, layerFor, parentKey, requireLayer, resolveRef, staticIds } from '../intents/common';
import { finiteCost, rasterPixels } from './cost';
import { defineCommand, DOCUMENT_RESOURCES, resourceFootprint } from './registry';

// ---------------------------------------------------------------------------
// Shared helpers

type Rect = { x: number; y: number; width: number; height: number };

/** Validation of an ID-addressed resource entry; resources are not layers, so a missing one is COMMAND_REJECTED. */
function requireEntry<T extends { id: string }>(items: readonly T[], id: string, what: string, diagnostics: Diagnostic[]): T | undefined {
  const found = items.find((item) => item.id === id);
  if (!found) diagnostics.push(error('COMMAND_REJECTED', `${what} ${JSON.stringify(id).slice(0, 80)} does not exist.`));
  return found;
}

function missing(what: string, id: string): IntentRejection {
  return new IntentRejection('COMMAND_REJECTED', `${what} ${JSON.stringify(id).slice(0, 80)} does not exist.`);
}

/** Validates that `params[field]` names an existing entry of the resource list. */
function check<T extends { id: string }>(items: (document: EditorDocument) => T[], what: string, field: string) {
  return (document: EditorDocument, params: Record<string, unknown>): Diagnostic[] => {
    const diagnostics: Diagnostic[] = [];
    requireEntry(items(document), String(params[field]), what, diagnostics);
    return diagnostics;
  };
}

/** Layer write check: native rules for the user, LAYER_LOCKED for agents. */
function writableLayer(document: EditorDocument, ref: Ref, context: ValidateContext, diagnostics: Diagnostic[], mode: 'any' | 'position' | 'pixels' = 'any'): Layer | null {
  const layer = layerFor(document, ref, context, diagnostics);
  if (layer) checkUnlocked(document, layer, mode, diagnostics, context.policy);
  return layer;
}

function withKeys(footprint: Footprint, keys: { reads?: string[]; writes?: string[] }): Footprint {
  return {
    reads: [...new Set([...footprint.reads, ...(keys.reads ?? [])])],
    writes: [...new Set([...footprint.writes, ...(keys.writes ?? [])])],
    structural: footprint.structural,
    document: footprint.document,
  };
}

const R = DOCUMENT_RESOURCES;

// ---------------------------------------------------------------------------
// Notes (NotesPanel, Note tool)

const NOTE_LIMIT = 100;
type NoteColor = DocumentNote['color'];

export const addNote = defineCommand<{ x: number; y: number; text?: string; author?: string; color?: NoteColor }>({
  id: 'notes.add',
  group: 'resources',
  backs: ['panel.notes.add-center', 'tool.note'],
  label: () => 'Add note',
  footprint: () => resourceFootprint(R.notes),
  validate: (document) => documentNotes(document).length >= NOTE_LIMIT ? [error('LIMIT_EXCEEDED', `A document holds at most ${NOTE_LIMIT} notes.`)] : [],
  compile: (document, params) => {
    const note = createDocumentNote(document, { x: params.x, y: params.y });
    if (params.text !== undefined) note.text = params.text;
    if (params.author !== undefined) note.author = params.author;
    if (params.color !== undefined) note.color = params.color;
    return [new SetDocumentNotesCommand([...documentNotes(document), note], 'Add note')];
  },
});

type NotePatch = { note: string; text?: string; author?: string; color?: NoteColor; resolved?: boolean; x?: number; y?: number };

function notePatchLabel(params: NotePatch): string {
  const fields = Object.keys(params).filter((key) => key !== 'note');
  if (fields.length === 1 && fields[0] === 'resolved') return params.resolved ? 'Resolve note' : 'Reopen note';
  return 'Update note';
}

export const updateNote = defineCommand<NotePatch>({
  id: 'notes.update',
  group: 'resources',
  backs: ['panel.notes.toggle-resolved', 'panel.notes.edit-text', 'panel.notes.edit-author', 'panel.notes.edit-color'],
  label: (params) => notePatchLabel(params),
  footprint: () => resourceFootprint(R.notes),
  validate: check(documentNotes, 'Note', 'note'),
  compile: (document, params) => {
    const notes = documentNotes(document);
    if (!notes.some((note) => note.id === params.note)) throw missing('Note', params.note);
    const patch: Partial<DocumentNote> = {};
    if (params.text !== undefined) patch.text = params.text;
    if (params.author !== undefined) patch.author = params.author;
    if (params.color !== undefined) patch.color = params.color;
    if (params.resolved !== undefined) patch.resolved = params.resolved;
    if (params.x !== undefined) patch.x = Math.max(0, Math.min(document.width, params.x));
    if (params.y !== undefined) patch.y = Math.max(0, Math.min(document.height, params.y));
    return [new SetDocumentNotesCommand(notes.map((note) => note.id === params.note ? { ...note, ...patch, updatedAt: new Date().toISOString() } : note), notePatchLabel(params))];
  },
});

export const deleteNote = defineCommand<{ note: string }>({
  id: 'notes.delete',
  group: 'resources',
  backs: ['panel.notes.delete'],
  label: () => 'Delete note',
  footprint: () => resourceFootprint(R.notes),
  validate: check(documentNotes, 'Note', 'note'),
  compile: (document, params) => [new SetDocumentNotesCommand(documentNotes(document).filter((note) => note.id !== params.note), 'Delete note')],
});

// ---------------------------------------------------------------------------
// Artboards, export assets, slices (ExportPanel)

export const addArtboard = defineCommand<{ name?: string; rect?: Rect; background?: Artboard['background'] }>({
  id: 'artboards.add',
  group: 'resources',
  backs: ['panel.export.add-artboard'],
  label: () => 'Add artboard',
  footprint: () => resourceFootprint(R.artboards),
  validate: () => [],
  compile: (document, params) => {
    const board = createArtboard(document);
    if (params.name !== undefined) board.name = params.name;
    if (params.rect) board.rect = { ...params.rect };
    if (params.background) board.background = params.background;
    return [new SetArtboardsCommand([...artboards(document), board], 'Add artboard')];
  },
});

export const updateArtboard = defineCommand<{ artboard: string; name?: string; rect?: Rect; background?: Artboard['background'] }>({
  id: 'artboards.update',
  group: 'resources',
  backs: ['panel.export.edit-artboard-name', 'panel.export.edit-artboard-rect', 'panel.export.edit-artboard-background'],
  label: () => 'Update artboards',
  footprint: () => resourceFootprint(R.artboards),
  validate: check(artboards, 'Artboard', 'artboard'),
  compile: (document, params) => {
    const { artboard, ...patch } = params;
    return [new SetArtboardsCommand(artboards(document).map((board) => board.id === artboard ? { ...board, ...patch, rect: patch.rect ?? board.rect } : board))];
  },
});

export const deleteArtboard = defineCommand<{ artboard: string }>({
  id: 'artboards.delete',
  group: 'resources',
  backs: ['panel.export.delete-artboard'],
  label: () => 'Delete artboard',
  footprint: () => resourceFootprint(R.artboards),
  validate: check(artboards, 'Artboard', 'artboard'),
  compile: (document, params) => [new SetArtboardsCommand(artboards(document).filter((board) => board.id !== params.artboard), 'Delete artboard')],
});

type AssetFields = { name?: string; format?: ExportAsset['format']; scale?: ExportAsset['scale']; trim?: boolean };

export const addExportAsset = defineCommand<AssetFields & { layer?: Ref }>({
  id: 'exportAssets.add',
  group: 'resources',
  backs: ['panel.export.add-document-asset', 'panel.export.add-layer-asset'],
  label: (params) => params.layer === undefined ? 'Add document export' : 'Add layer export',
  footprint: (_document, params, aliases) => withKeys(resourceFootprint(R.exportAssets), { reads: params.layer === undefined ? [] : staticIds([params.layer], aliases) }),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    if (params.layer !== undefined) layerFor(document, params.layer, context, diagnostics);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = params.layer === undefined ? undefined : requireLayer(document, params.layer, aliases);
    const created = createExportAsset(document, layer?.id);
    const asset: ExportAsset = layer ? { ...created, name: layer.name } : created;
    if (params.name !== undefined) asset.name = params.name;
    if (params.format) asset.format = params.format;
    if (params.scale) asset.scale = params.scale;
    if (params.trim !== undefined) asset.trim = params.trim;
    return [new SetExportAssetsCommand([...exportAssets(document), asset], layer ? 'Add layer export' : 'Add document export')];
  },
});

export const updateExportAsset = defineCommand<AssetFields & { asset: string }>({
  id: 'exportAssets.update',
  group: 'resources',
  backs: ['panel.export.edit-asset'],
  label: () => 'Update export assets',
  footprint: () => resourceFootprint(R.exportAssets),
  validate: check(exportAssets, 'Export asset', 'asset'),
  compile: (document, params) => {
    const { asset: id, ...patch } = params;
    return [new SetExportAssetsCommand(exportAssets(document).map((asset) => asset.id === id ? { ...asset, ...patch } as ExportAsset : asset))];
  },
});

export const deleteExportAsset = defineCommand<{ asset: string }>({
  id: 'exportAssets.delete',
  group: 'resources',
  backs: ['panel.export.delete-asset'],
  label: () => 'Delete export asset',
  footprint: () => resourceFootprint(R.exportAssets),
  validate: check(exportAssets, 'Export asset', 'asset'),
  compile: (document, params) => [new SetExportAssetsCommand(exportAssets(document).filter((asset) => asset.id !== params.asset), 'Delete export asset')],
});

const SLICE_LIMIT = 64;
const sliceLimit = (document: EditorDocument): Diagnostic[] => slices(document).length >= SLICE_LIMIT ? [error('LIMIT_EXCEEDED', `A document holds at most ${SLICE_LIMIT} slices.`)] : [];

export const addSlice = defineCommand<{ rect?: Rect; name?: string; format?: Slice['format']; quality?: number }>({
  id: 'slices.add',
  group: 'resources',
  backs: ['panel.export.add-full-slice', 'panel.export.add-selection-slice'],
  label: (params) => params.rect ? 'Add selection slice' : 'Add full-document slice',
  footprint: () => resourceFootprint(R.slices),
  validate: (document) => sliceLimit(document),
  compile: (document, params) => {
    const slice = createSlice(document, params.rect, params.name);
    if (params.format) slice.format = params.format;
    if (params.quality !== undefined) slice.quality = params.quality;
    return [new SetSlicesCommand([...slices(document), slice], params.rect ? 'Add selection slice' : 'Add full-document slice')];
  },
});

export const slicesFromGuideCells = defineCommand<Record<string, never>>({
  id: 'slices.fromGuides',
  group: 'resources',
  backs: ['panel.export.slices-from-guides'],
  label: () => 'Create slices from guides',
  footprint: () => withKeys(resourceFootprint(R.slices), { reads: [R.guides] }),
  validate: (document) => sliceLimit(document),
  compile: (document) => {
    const existing = slices(document);
    let generated: Slice[];
    try {
      generated = slicesFromGuides(document, SLICE_LIMIT - existing.length);
    } catch (reason) {
      throw new IntentRejection('LIMIT_EXCEEDED', reason instanceof Error ? reason.message : 'Slices could not be created.');
    }
    return [new SetSlicesCommand([...existing, ...generated], 'Create slices from guides')];
  },
});

export const updateSlice = defineCommand<{ slice: string; name?: string; rect?: Rect; format?: Slice['format']; quality?: number }>({
  id: 'slices.update',
  group: 'resources',
  backs: ['panel.export.edit-slice'],
  label: () => 'Update slices',
  footprint: () => resourceFootprint(R.slices),
  validate: check(slices, 'Slice', 'slice'),
  compile: (document, params) => {
    const { slice: id, ...patch } = params;
    return [new SetSlicesCommand(slices(document).map((slice) => slice.id === id ? { ...slice, ...patch, rect: patch.rect ?? slice.rect } : slice))];
  },
});

export const deleteSlice = defineCommand<{ slice: string }>({
  id: 'slices.delete',
  group: 'resources',
  backs: ['panel.export.delete-slice'],
  label: () => 'Delete slice',
  footprint: () => resourceFootprint(R.slices),
  validate: check(slices, 'Slice', 'slice'),
  compile: (document, params) => [new SetSlicesCommand(slices(document).filter((slice) => slice.id !== params.slice), 'Delete slice')],
});

// ---------------------------------------------------------------------------
// Alpha channels (ChannelsPanel rename/delete)

export const renameChannel = defineCommand<{ channel: string; name: string }>({
  id: 'channels.rename',
  group: 'resources',
  backs: ['panel.channels.rename-alpha'],
  label: () => 'Rename alpha channel',
  footprint: () => resourceFootprint(R.channels),
  validate: check(alphaChannels, 'Alpha channel', 'channel'),
  compile: (document, params) => [new SetAlphaChannelsCommand(alphaChannels(document).map((channel) => channel.id === params.channel ? { ...channel, name: params.name } : channel), 'Rename alpha channel')],
});

export const deleteChannel = defineCommand<{ channel: string }>({
  id: 'channels.delete',
  group: 'resources',
  backs: ['panel.channels.delete-alpha'],
  label: () => 'Delete alpha channel',
  footprint: () => resourceFootprint(R.channels),
  validate: check(alphaChannels, 'Alpha channel', 'channel'),
  compile: (document, params) => [new SetAlphaChannelsCommand(alphaChannels(document).filter((channel) => channel.id !== params.channel), 'Delete alpha channel')],
});

// ---------------------------------------------------------------------------
// Perspective planes (VanishingPointDialog, Move-tool corner handles)

const PLANE_LIMIT = 8;
interface PlaneInput { id?: string; name: string; mode?: PerspectiveGridMode; corners: readonly Point[] }

/** The dialog passes complete planes, kept as they are (byte parity); planes without an id get a new one. */
function planesOf(params: { planes: readonly PlaneInput[] }): PerspectivePlane[] {
  return params.planes.map((plane) => plane.id !== undefined && plane.mode !== undefined
    ? structuredClone(plane) as PerspectivePlane
    : { ...createPerspectivePlane(plane.corners as unknown as Quad, plane.name, plane.mode ?? 'two-point'), ...(plane.id === undefined ? {} : { id: plane.id }) });
}

export const setPerspectivePlanes = defineCommand<{ planes: PlaneInput[]; active?: string }>({
  id: 'perspective.setPlanes',
  group: 'resources',
  backs: ['dialog.vanishing-point.save', 'dialog.vanishing-point.delete-last', 'tool.move.perspective-plane-corner'],
  label: () => 'Update perspective grids',
  footprint: () => resourceFootprint(R.perspective),
  validate: (_document, params) => {
    if (params.planes.length > PLANE_LIMIT) return [error('LIMIT_EXCEEDED', 'Perspective Grid is limited to eight planes per document.')];
    const ids = params.planes.flatMap((plane) => plane.id === undefined ? [] : [plane.id]);
    if (new Set(ids).size !== ids.length || params.planes.some((plane) => plane.corners.length !== 4 || !validPerspectiveQuad(plane.corners as unknown as Quad))) {
      return [error('COMMAND_REJECTED', 'Perspective Grid planes must have unique IDs and valid convex corners.')];
    }
    if (params.active !== undefined && !ids.includes(params.active)) return [error('COMMAND_REJECTED', `Plane ${JSON.stringify(params.active).slice(0, 80)} is not one of the planes.`)];
    return [];
  },
  compile: (_document, params) => [new SetPerspectivePlanesCommand(planesOf(params), params.active)],
});

function planeFor(document: EditorDocument, id: string | undefined): PerspectivePlane | undefined {
  return id === undefined ? activePerspectivePlane(document) : perspectivePlanes(document).find((plane) => plane.id === id);
}

const MAP_PIXEL_LIMIT = 16_000_000;

export const mapRasterToPlane = defineCommand<{ layer: Ref; plane?: string }>({
  id: 'perspective.mapRaster',
  group: 'pixels',
  backs: ['dialog.vanishing-point.map-raster'],
  label: () => 'Map raster to Perspective Grid',
  // Inverse homography and bilinear sampling per pixel, plus the copies (see ./cost).
  cost: (params, document) => finiteCost(rasterPixels(document, params.layer) * 8),
  footprint: (_document, params, aliases) => withKeys({ reads: staticIds([params.layer], aliases), writes: staticIds([params.layer], aliases), structural: false, document: false }, { reads: [R.perspective] }),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = writableLayer(document, params.layer, context, diagnostics, 'pixels');
    if (!planeFor(document, params.plane)) diagnostics.push(error('COMMAND_REJECTED', params.plane === undefined ? 'The document has no perspective plane.' : `Plane ${JSON.stringify(params.plane).slice(0, 80)} does not exist.`));
    if (!layer) return diagnostics;
    if (layer.kind !== 'raster') diagnostics.push(error('UNSUPPORTED_FOR_KIND', 'Perspective mapping needs a raster layer.', layer.id));
    else if (layer.raster.width * layer.raster.height > MAP_PIXEL_LIMIT) diagnostics.push(error('LIMIT_EXCEEDED', 'Perspective mapping is limited to 16 megapixels per layer.', layer.id));
    const effective = layerDocumentTransform(document.root, layer.id);
    if (!effective || !invertMatrix(effective)) diagnostics.push(error('COMMAND_REJECTED', 'The active layer transform is not invertible.', layer.id));
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireLayer(document, params.layer, aliases);
    const plane = planeFor(document, params.plane);
    const effective = layerDocumentTransform(document.root, layer.id);
    const inverse = effective ? invertMatrix(effective) : null;
    if (layer.kind !== 'raster' || !plane || !inverse) throw new IntentRejection('COMMAND_REJECTED', 'Perspective mapping needs a raster layer and a plane.', layer.id);
    const localCorners = plane.corners.map((corner) => transformPoint(inverse, corner)) as unknown as Quad;
    if (!validPerspectiveQuad(localCorners)) throw new IntentRejection('COMMAND_REJECTED', 'The perspective plane is invalid.', layer.id);
    return [new ReplaceRasterCommand(layer.id, warpRasterToPerspectivePlane(layer.raster, localCorners), 'Map raster to Perspective Grid')];
  },
});

// ---------------------------------------------------------------------------
// Layer comps (LayerCompsPanel)

const compLabel = (verb: string, name: string) => `${verb} comp “${name}”`;
const contentIds = (document: EditorDocument) => walkLayers(document.root).filter((layer) => layer.id !== document.root.id).map((layer) => layer.id);

function compOf(document: EditorDocument, id: string): LayerComp | undefined {
  return layerComps(document).find((comp) => comp.id === id);
}

export const saveLayerComp = defineCommand<{ name?: string }>({
  id: 'layerComps.save',
  group: 'resources',
  backs: ['panel.layer-comps.save'],
  label: (params, document) => compLabel('Save', (params.name ?? '').trim() || `Comp ${layerComps(document).length + 1}`),
  // Captures every layer's appearance, so it depends on all of them.
  footprint: (document) => withKeys(resourceFootprint(R.comps), { reads: contentIds(document) }),
  validate: () => [],
  compile: (document, params) => {
    const comp = captureLayerComp(document, params.name ?? '');
    return [new SetLayerCompsCommand([...layerComps(document), comp], compLabel('Save', comp.name))];
  },
});

export const updateLayerComp = defineCommand<{ comp: string }>({
  id: 'layerComps.update',
  group: 'resources',
  backs: ['panel.layer-comps.update'],
  label: (params, document) => compLabel('Update', compOf(document, params.comp)?.name ?? ''),
  footprint: (document) => withKeys(resourceFootprint(R.comps), { reads: contentIds(document) }),
  validate: check(layerComps, 'Layer comp', 'comp'),
  compile: (document, params) => {
    const comps = layerComps(document);
    const comp = comps.find((entry) => entry.id === params.comp);
    if (!comp) throw missing('Layer comp', params.comp);
    const updated = { ...captureLayerComp(document, comp.name), id: comp.id, createdAt: comp.createdAt };
    return [new SetLayerCompsCommand(comps.map((entry) => entry.id === comp.id ? updated : entry), compLabel('Update', comp.name))];
  },
});

/** Layers an apply would change, with the lock each change needs. */
function compTargets(document: EditorDocument, comp: LayerComp): { layer: Layer; mode: 'any' | 'position' }[] {
  return Object.entries(comp.layers).flatMap(([id, appearance]) => {
    const layer = findLayer(document.root, id);
    if (!layer) return [];
    const moves = JSON.stringify(layer.transform) !== JSON.stringify(appearance.transform);
    const changes = moves || layer.visible !== appearance.visible || layer.opacity !== appearance.opacity || layer.fillOpacity !== appearance.fillOpacity || layer.blendMode !== appearance.blendMode;
    return changes ? [{ layer, mode: moves ? 'position' as const : 'any' as const }] : [];
  });
}

export const applyLayerComp = defineCommand<{ comp: string }>({
  id: 'layerComps.apply',
  group: 'resources',
  backs: ['panel.layer-comps.apply'],
  label: (params, document) => compLabel('Apply', compOf(document, params.comp)?.name ?? ''),
  footprint: (document, params) => {
    const comp = compOf(document, params.comp);
    const ids = comp ? Object.keys(comp.layers).filter((id) => findLayer(document.root, id)) : [];
    return { reads: [R.comps, ...ids], writes: ids, structural: false, document: false };
  },
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const comp = requireEntry(layerComps(document), params.comp, 'Layer comp', diagnostics);
    if (!comp) return diagnostics;
    if (!Object.keys(comp.layers).some((id) => findLayer(document.root, id))) diagnostics.push(error('COMMAND_REJECTED', 'None of this comp’s layers exist in the current document.'));
    for (const { layer, mode } of compTargets(document, comp)) checkUnlocked(document, layer, mode, diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, params) => {
    const comp = compOf(document, params.comp);
    if (!comp) throw missing('Layer comp', params.comp);
    return [new ApplyLayerCompCommand(comp)];
  },
});

export const deleteLayerComp = defineCommand<{ comp: string }>({
  id: 'layerComps.delete',
  group: 'resources',
  backs: ['panel.layer-comps.delete'],
  label: (params, document) => compLabel('Delete', compOf(document, params.comp)?.name ?? ''),
  footprint: () => resourceFootprint(R.comps),
  validate: check(layerComps, 'Layer comp', 'comp'),
  compile: (document, params) => {
    const comps = layerComps(document);
    const comp = comps.find((entry) => entry.id === params.comp);
    if (!comp) throw missing('Layer comp', params.comp);
    return [new SetLayerCompsCommand(comps.filter((entry) => entry.id !== comp.id), compLabel('Delete', comp.name))];
  },
});

// ---------------------------------------------------------------------------
// Global swatches and colour reduction (GlobalSwatchesPanel)

export const addSwatch = defineCommand<{ color: string; name?: string }>({
  id: 'swatches.add',
  group: 'resources',
  backs: ['panel.global-swatches.add'],
  label: () => 'Add global swatch',
  footprint: () => resourceFootprint(R.swatches),
  validate: () => [],
  compile: (document, params) => [new SetGlobalSwatchesCommand([...globalSwatches(document), createGlobalSwatch(params.color, params.name)], 'Add global swatch')],
});

const linkedLayerIds = (document: EditorDocument, swatch: string) => [...new Set(linkedPaints(document, swatch).map((paint) => paint.layerId))];

export const updateSwatch = defineCommand<{ swatch: string; color: string }>({
  id: 'swatches.update',
  group: 'resources',
  backs: ['panel.global-swatches.edit-color'],
  label: () => 'Edit global swatch',
  // Linked fills, strokes and text are recoloured with the swatch.
  footprint: (document, params) => withKeys(resourceFootprint(R.swatches), { reads: linkedLayerIds(document, params.swatch), writes: linkedLayerIds(document, params.swatch) }),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    if (!requireEntry(globalSwatches(document), params.swatch, 'Global swatch', diagnostics)) return diagnostics;
    for (const id of linkedLayerIds(document, params.swatch)) {
      const layer = findLayer(document.root, id);
      if (layer) checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    }
    return diagnostics;
  },
  compile: (_document, params) => [new UpdateGlobalSwatchCommand(params.swatch, params.color)],
});

export const deleteSwatch = defineCommand<{ swatch: string }>({
  id: 'swatches.delete',
  group: 'resources',
  backs: ['panel.global-swatches.delete'],
  label: () => 'Delete global swatch',
  footprint: () => resourceFootprint(R.swatches),
  validate: check(globalSwatches, 'Global swatch', 'swatch'),
  compile: (document, params) => [new SetGlobalSwatchesCommand(globalSwatches(document).filter((swatch) => swatch.id !== params.swatch), 'Delete global swatch')],
});

export const applySwatch = defineCommand<{ swatch: string; layer: Ref; objects?: string[] }>({
  id: 'swatches.apply',
  group: 'resources',
  backs: ['panel.global-swatches.apply-to-selection'],
  label: () => 'Apply global swatch',
  footprint: (_document, params, aliases) => {
    const ids = staticIds([params.layer], aliases);
    return { reads: [R.swatches, ...ids], writes: ids, structural: false, document: false };
  },
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    requireEntry(globalSwatches(document), params.swatch, 'Global swatch', diagnostics);
    const layer = writableLayer(document, params.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (layer.kind !== 'vector' && layer.kind !== 'text') diagnostics.push(error('UNSUPPORTED_FOR_KIND', 'Select vector artwork or text to apply a global swatch.', layer.id));
    else if (layer.kind === 'vector' && !layer.vector.objects.some((object) => params.objects === undefined || params.objects.includes(object.id))) diagnostics.push(error('COMMAND_REJECTED', 'Select at least one vector object.', layer.id));
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireLayer(document, params.layer, aliases);
    const swatch = globalSwatches(document).find((entry) => entry.id === params.swatch);
    if (!swatch) throw missing('Global swatch', params.swatch);
    // Omitted objects: every object of a vector layer.
    const objects = params.objects ?? (layer.kind === 'vector' ? layer.vector.objects.map((object) => object.id) : []);
    return [new ApplyGlobalSwatchCommand(layer.id, objects, swatch)];
  },
});

export const reduceColours = defineCommand<{ layer: Ref; colours: number }>({
  id: 'vector.reduceColours',
  group: 'vector',
  backs: ['panel.global-swatches.reduce-colours-slider', 'panel.global-swatches.reduce-colours-preset', 'panel.global-swatches.reduce-colours-all'],
  label: () => 'Reduce colours',
  footprint: (_document, params, aliases) => ({ reads: staticIds([params.layer], aliases), writes: staticIds([params.layer], aliases), structural: false, document: false }),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = writableLayer(document, params.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (layer.kind !== 'vector') diagnostics.push(error('UNSUPPORTED_FOR_KIND', 'Select vector artwork to reduce its colours.', layer.id));
    // ReduceColoursCommand records its origin before this check; refusing here keeps a rejected run traceless.
    else if (layerColourStack(layer).entries.length === 0) diagnostics.push(error('COMMAND_REJECTED', 'This layer has no filled artwork.', layer.id));
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => [new ReduceColoursCommand(requireLayer(document, params.layer, aliases).id, params.colours)],
});

// ---------------------------------------------------------------------------
// Recorded actions and tool presets

export const renameAction = defineCommand<{ action: string; name: string }>({
  id: 'actions.rename',
  group: 'resources',
  backs: ['panel.actions.rename'],
  label: () => 'Rename action',
  footprint: () => resourceFootprint(R.actions),
  validate: check(documentActions, 'Action', 'action'),
  compile: (_document, params) => [new RenameActionCommand(params.action, params.name)],
});

export const deleteAction = defineCommand<{ action: string }>({
  id: 'actions.delete',
  group: 'resources',
  backs: ['panel.actions.delete'],
  label: () => 'Delete action',
  footprint: () => resourceFootprint(R.actions),
  validate: check(documentActions, 'Action', 'action'),
  compile: (_document, params) => [new DeleteActionCommand(params.action)],
});

export const renameToolPreset = defineCommand<{ preset: string; name: string }>({
  id: 'toolPresets.rename',
  group: 'resources',
  backs: ['dialog.tool-presets.rename'],
  label: () => 'Rename tool preset',
  footprint: () => resourceFootprint(R.presets),
  validate: check(toolPresets, 'Tool preset', 'preset'),
  compile: (document, params) => [new SetToolPresetsCommand(toolPresets(document).map((item) => item.id === params.preset ? { ...item, name: params.name.slice(0, 100) } : item), 'Rename tool preset')],
});

export const deleteToolPreset = defineCommand<{ preset: string }>({
  id: 'toolPresets.delete',
  group: 'resources',
  backs: ['dialog.tool-presets.delete'],
  label: () => 'Delete tool preset',
  footprint: () => resourceFootprint(R.presets),
  validate: check(toolPresets, 'Tool preset', 'preset'),
  compile: (document, params) => [new SetToolPresetsCommand(toolPresets(document).filter((item) => item.id !== params.preset), 'Delete tool preset')],
});

// ---------------------------------------------------------------------------
// Layer operations of these panels: image trace, group disclosure

export const imageTrace = defineCommand<{ layer: Ref; threshold?: number; invert?: boolean }>({
  id: 'vector.imageTrace',
  createsLayer: true,
  group: 'vector',
  backs: ['action.image-trace'],
  label: (params, document) => {
    const id = resolveRef(params.layer, new Map());
    const layer = id === undefined ? undefined : findLayer(document.root, id);
    return layer ? `Add ${layer.name} trace` : 'Image Trace';
  },
  // Adds a sibling directly above the source: reads the source and its parent's child list.
  footprint: (document, params, aliases) => {
    const ids = staticIds([params.layer], aliases);
    return { reads: [...ids, ...ids.map((id) => parentKey(document, id))], writes: [], structural: true, document: false };
  },
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, params.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (layer.kind !== 'raster') diagnostics.push(error('UNSUPPORTED_FOR_KIND', 'Select a raster layer before using Image Trace.', layer.id));
    const location = findLayerParent(document.root, layer.id);
    if (location) checkContainerUnlocked(document, location.parent, diagnostics, layer.id, context.policy);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireLayer(document, params.layer, aliases);
    if (layer.kind !== 'raster') throw new IntentRejection('UNSUPPORTED_FOR_KIND', 'Select a raster layer before using Image Trace.', layer.id);
    const traced = traceRasterLayer(layer, { threshold: params.threshold ?? 128, invert: params.invert ?? false });
    if (!traced) throw new IntentRejection('COMMAND_REJECTED', 'Image Trace found no dark regions or the result was too complex.', layer.id);
    const location = findLayerParent(document.root, layer.id);
    return [new AddLayerCommand(traced, location?.parent === document.root ? 'root' : location?.parent.id ?? 'root', location?.index ?? 0)];
  },
});

export const setGroupExpanded = defineCommand<{ layer: Ref; expanded: boolean }>({
  id: 'layer.setExpanded',
  group: 'layers',
  backs: ['panel.layers.group-expand-key', 'panel.layers.group-expand-toggle', 'panel.layers.smart-object-expand', 'action.edit-smart-object'],
  label: (params) => params.expanded ? 'Expand group' : 'Collapse group',
  // Disclosure is panel state stored with the group, not content: it reads the group but
  // claims no write, so it does not conflict with edits (as SetGroupExpandedCommand never
  // did in the ledger), and locks do not apply.
  footprint: (_document, params, aliases) => ({ reads: staticIds([params.layer], aliases), writes: [], structural: false, document: false }),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, params.layer, context, diagnostics);
    if (layer && layer.kind !== 'group') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${contentLabel(layer.name, 80)} is not a group.`, layer.id));
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => [new SetGroupExpandedCommand(requireLayer(document, params.layer, aliases).id, params.expanded)],
});

export const RESOURCE_COMMANDS = [
  addNote, updateNote, deleteNote,
  addArtboard, updateArtboard, deleteArtboard,
  addExportAsset, updateExportAsset, deleteExportAsset,
  addSlice, slicesFromGuideCells, updateSlice, deleteSlice,
  renameChannel, deleteChannel,
  setPerspectivePlanes, mapRasterToPlane,
  saveLayerComp, updateLayerComp, applyLayerComp, deleteLayerComp,
  addSwatch, updateSwatch, deleteSwatch, applySwatch, reduceColours,
  renameAction, deleteAction,
  renameToolPreset, deleteToolPreset,
  imageTrace, setGroupExpanded,
] as const;
